import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Link2, Quote } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Reveal } from "../components/Motion";
import { getPost, POSTS } from "../lib/blog";

const NotFound = () => (
  <div className="pt-44 pb-40 text-center" data-testid="insight-not-found">
    <h1 className="font-serif-lux text-6xl text-[#1A1A1A]">Article not found</h1>
    <Link to="/insights" className="inline-block mt-8 overline text-[#C19A6B] link-sweep">← Back to insights</Link>
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return <h2 className="font-serif-lux text-4xl md:text-5xl text-[#1A1A1A] tracking-tight mt-16 mb-6">{block.text}</h2>;
    case "lead":
      return <p className="font-serif-lux text-2xl md:text-3xl text-[#1A1A1A] leading-[1.35] mb-4">{block.text}</p>;
    case "p":
      return <p className="font-sans-lux text-lg text-[#3A3A3A] leading-relaxed mb-6 max-w-3xl">{block.text}</p>;
    case "blockers":
      return (
        <div className="space-y-4 my-8">
          {block.items.map((it) => (
            <div key={it.n} className="border border-[#E5DFD3] bg-[#F5F2EB] p-7 md:p-9">
              <div className="flex items-start gap-5">
                <span className="font-serif-lux text-5xl md:text-6xl text-[#C19A6B] leading-none">{it.n}</span>
                <div className="flex-1">
                  <h3 className="font-serif-lux text-2xl md:text-3xl text-[#1A1A1A]">{it.title}</h3>
                  <p className="font-sans-lux text-[#3A3A3A] leading-relaxed mt-3">{it.body}</p>
                  <div className="mt-5 border-l-2 border-[#C19A6B] pl-4">
                    <p className="overline text-[#6E7765] mb-1">Diagnostic question</p>
                    <p className="font-serif-lux text-xl text-[#1A1A1A] italic">{it.diagnostic}</p>
                  </div>
                  <p className="font-sans-lux text-sm text-[#5C5C5C] mt-4">
                    <span className="font-semibold text-[#1A1A1A]">Why it stalls deals: </span>{it.stalls}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    case "table":
      return (
        <div className="my-8 overflow-x-auto border border-[#E5DFD3]">
          <table className="w-full text-left font-sans-lux text-sm md:text-base border-collapse">
            <thead className="bg-[#1A1A1A] text-[#FDFBF7]">
              <tr>
                {block.head.map((h, i) => (
                  <th key={i} className="px-5 py-4 font-medium align-top">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="border-t border-[#E5DFD3] align-top even:bg-[#F5F2EB]">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-5 py-4 ${ci === 0 ? "font-semibold text-[#1A1A1A] whitespace-nowrap" : "text-[#3A3A3A]"}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "steps":
      return (
        <ol className="my-8 space-y-6 counter-reset">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-5">
              <span className="shrink-0 w-10 h-10 rounded-full bg-[#1A1A1A] text-[#FDFBF7] flex items-center justify-center font-serif-lux text-lg">{i + 1}</span>
              <div>
                <h4 className="font-serif-lux text-2xl text-[#1A1A1A]">{it.title}</h4>
                <p className="font-sans-lux text-[#3A3A3A] leading-relaxed mt-2 max-w-3xl">{it.body}</p>
              </div>
            </li>
          ))}
        </ol>
      );
    case "callout":
      return (
        <div className="my-10 bg-[#1A1A1A] text-[#FDFBF7] p-8 md:p-12 relative">
          <Quote size={40} className="text-[#C19A6B] mb-5" />
          <p className="font-serif-lux text-2xl md:text-4xl leading-[1.3] tracking-tight">{block.text}</p>
        </div>
      );
    default:
      return null;
  }
};

const mdComponents = {
  h2: ({ children }) => <h2 className="font-serif-lux text-4xl md:text-5xl text-[#1A1A1A] tracking-tight mt-14 mb-5">{children}</h2>,
  h3: ({ children }) => <h3 className="font-serif-lux text-2xl md:text-3xl text-[#1A1A1A] tracking-tight mt-10 mb-3">{children}</h3>,
  p: ({ children }) => <p className="font-sans-lux text-lg text-[#3A3A3A] leading-relaxed mb-6 max-w-3xl">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-[#1A1A1A]">{children}</strong>,
  em: ({ children }) => <em className="text-[#5C5C5C]">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-6 space-y-2 mb-6 font-sans-lux text-lg text-[#3A3A3A] max-w-3xl">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 space-y-2 mb-6 font-sans-lux text-lg text-[#3A3A3A] max-w-3xl">{children}</ol>,
  a: ({ children, href }) =>
    href && href.startsWith("/") ? (
      <Link to={href} className="text-[#C19A6B] underline underline-offset-2 hover:text-[#1A1A1A]">{children}</Link>
    ) : (
      <a href={href} className="text-[#C19A6B] underline underline-offset-2 hover:text-[#1A1A1A]">{children}</a>
    ),
  blockquote: ({ children }) => <blockquote className="border-l-2 border-[#C19A6B] pl-5 my-6 font-serif-lux text-2xl text-[#1A1A1A] italic">{children}</blockquote>,
  table: ({ children }) => <div className="my-8 overflow-x-auto border border-[#E5DFD3]"><table className="w-full text-left font-sans-lux text-sm md:text-base border-collapse">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-[#1A1A1A] text-[#FDFBF7]">{children}</thead>,
  th: ({ children }) => <th className="px-5 py-4 font-medium align-top">{children}</th>,
  tr: ({ children }) => <tr className="border-t border-[#E5DFD3] align-top even:bg-[#F5F2EB]">{children}</tr>,
  td: ({ children }) => <td className="px-5 py-4 text-[#3A3A3A] align-top">{children}</td>,
};

export default function InsightDetail() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <NotFound />;

  const copyLink = async () => {
    const url = `${window.location.origin}/insights/${post.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: url });
    } catch {
      toast.info("Copy this link", { description: url });
    }
  };

  const others = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <article data-testid="insight-detail-page" className="pt-28 md:pt-36">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <Link to="/insights" data-testid="back-to-insights" className="inline-flex items-center gap-2 overline text-[#5C5C5C] hover:text-[#1A1A1A] mb-10">
          <ArrowLeft size={15} /> All insights
        </Link>

        <div className="flex items-center gap-4 mb-6">
          <span className="overline text-[#C19A6B]">{post.category}</span>
          <span className="font-sans-lux text-sm text-[#5C5C5C]">{post.date} · {post.readTime}</span>
        </div>

        <h1 className="font-serif-lux text-[#1A1A1A] text-5xl md:text-7xl leading-[0.95] tracking-tight">{post.title}</h1>

        <div className="flex items-center justify-between gap-4 mt-8 pb-8 border-b border-[#E5DFD3]">
          <p className="font-sans-lux text-sm text-[#5C5C5C]">By {post.author}</p>
          <button
            onClick={copyLink}
            data-testid="copy-link-button"
            className="inline-flex items-center gap-2 border border-[#E5DFD3] hover:border-[#1A1A1A] px-5 py-3 overline text-[#1A1A1A] transition-colors"
          >
            <Link2 size={15} /> Copy link
          </button>
        </div>

        <div className="mt-10">
          {post.markdown ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {post.markdown}
            </ReactMarkdown>
          ) : (
            post.blocks.map((b, i) => <Block key={i} block={b} />)
          )}
        </div>
      </div>

      {/* Footer CTA */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 py-16">
        <div className="border border-[#E5DFD3] bg-[#F5F2EB] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="overline text-[#C19A6B]">Work with the team</p>
            <p className="font-serif-lux text-2xl md:text-3xl text-[#1A1A1A] mt-2">Want this discipline selling your project?</p>
          </div>
          <Link to="/contact" data-testid="insight-cta" className="shrink-0 inline-flex items-center gap-3 bg-[#1A1A1A] text-[#FDFBF7] btn-lux btn-lux-invert px-8 py-4 overline">
            Get in touch <ArrowUpRight size={15} />
          </Link>
        </div>
      </section>

      {others.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28">
          <div className="border-t border-[#E5DFD3] pt-10 mb-10">
            <h2 className="font-serif-lux text-4xl text-[#1A1A1A] tracking-tight">More insights</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {others.map((p) => (
              <Link key={p.slug} to={`/insights/${p.slug}`} className="group border border-[#E5DFD3] p-8 hover:border-[#C19A6B] transition-colors">
                <span className="overline text-[#C19A6B]">{p.category}</span>
                <h3 className="font-serif-lux text-2xl md:text-3xl text-[#1A1A1A] mt-3 group-hover:text-[#C19A6B] transition-colors">{p.title}</h3>
                <p className="font-sans-lux text-sm text-[#5C5C5C] mt-3">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
